// M6 — Generic table CRUD factory.
// Backed by the supabase-compat shim (which routes to labourcore-api).
// Preserves the same shape as the v49.0 module so call sites need no migration.

import { supabase, SUPABASE_READY } from "./supabase-client";

/* Per-table sort column override. */
const TABLE_ORDER_BY: Record<string, string> = {
  invitations:          "sent_at",
  messages:             "sent_at",
  approved_workers:     "added_at",
  discontinued_workers: "added_at",
};

export interface Subscription {
  unsubscribe: () => void;
}

export interface TableApi<T = Record<string, unknown>> {
  list(filter?: Partial<T> | null): Promise<T[] | null>;
  insert(row: Partial<T>): Promise<T | null>;
  update(id: string | number, patch: Partial<T>): Promise<T | null>;
  upsert(row: Partial<T>): Promise<T | null>;
  remove(id: string | number): Promise<boolean>;
  subscribe(handler: (payload: unknown) => void): Subscription;
}

export function table<T = Record<string, unknown>>(name: string): TableApi<T> {
  return {
    async list(filter) {
      if (!SUPABASE_READY) return null;
      const orderCol = TABLE_ORDER_BY[name] || "created_at";
      let q = supabase.from(name).select("*").order(orderCol, { ascending: false }).limit(500);
      if (filter && typeof filter === "object") {
        Object.entries(filter).forEach(([k, v]) => { q = q.eq(k, v as never); });
      }
      const { data, error } = await q;
      if (error) { console.warn("[LABOURCORE_API]", name, "list error:", error); return null; }
      return (data as T[]) || [];
    },
    async insert(row) {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.from(name).insert(row as never).select().single();
      if (error) { console.warn("[LABOURCORE_API]", name, "insert error:", error); return null; }
      return (data as T) || null;
    },
    async update(id, patch) {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.from(name).update(patch as never).eq("id", id as never).select().single();
      if (error) { console.warn("[LABOURCORE_API]", name, "update error:", error); return null; }
      return (data as T) || null;
    },
    async upsert(row) {
      if (!SUPABASE_READY) return null;
      const { data, error } = await supabase.from(name).upsert(row as never).select().single();
      if (error) { console.warn("[LABOURCORE_API]", name, "upsert error:", error); return null; }
      return (data as T) || null;
    },
    async remove(id) {
      if (!SUPABASE_READY) return false;
      const { error } = await supabase.from(name).delete().eq("id", id as never);
      if (error) { console.warn("[LABOURCORE_API]", name, "remove error:", error); return false; }
      return true;
    },
    subscribe(handler) {
      // M6: Realtime not available via labourcore-api. No-op stub.
      console.warn(`[M6-realtime-stub] table("${name}").subscribe() — realtime not available`);
      return { unsubscribe: () => {} };
    },
  };
}
